import React, { useCallback, useLayoutEffect } from 'react';
import { StyleSheet, ScrollView, View } from 'react-native';
import { rem, Button, ButtonGroup, SettingsSection, SettingsItemBase, SettingsItemContent, SettingsItemDescription, } from 'design-system';
import TextLink from 'design-system/TextLink';
import StatusBar from 'design-system/StatusBar';
import { IconBank, IconWork } from 'design-system/icons';
import ScreenInfoHeader from 'design-system/ScreenInfoHeader';
import IconSirenOnSolid from 'design-system/icons/IconSirenOnSolid';
import Sentry from 'utils/sentry';
import useColors from 'hooks/useColors';
import { useMarginBottom } from 'hooks/useMarginBottom';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import useAppFrameDimensions from 'hooks/useAppFrameDimensions';
import HelpScreenButton from 'features/help/components/HelpScreenButton';
import { useCommonInvestStyles } from './styles';
import { useAmplitudeOnMountEvent } from '../utils/analytics';
import useNavigateToNextScreen from '../hooks/useNavigateToNextScreen';
import InvestHeaderWithHelpButton from '../components/InvestHeaderWithHelpButton';
import { strings as onboardingTitleStrings } from '../components/OnboardingTitle';
import { selectAnnualIncome, selectEmploymentStatus, selectFundingSource, selectNetworth } from '../reducers/selectors';
const strings = [
    'Affiliated with or work for a US registered broker-dealer or FINRA',
    'Senior executive at or a 10% or greater shareholder of a publicly traded company',
    'I am, or a immediate family member/relative is a senior political figure',
];
const CHOICES_MAP = {
    Yes: true,
    No: false,
};
const CHOICES = Object.keys(CHOICES_MAP);
const AffiliatesScreen = ({ navigation, route: { params }, navigation: { navigateDeprecated }, }) => {
    const colors = useColors();
    const navigateToNextScreen = useNavigateToNextScreen();
    const commonStyles = useCommonInvestStyles(params?.mountedOnAnotherScreen);
    useLayoutEffect(() => {
        navigation.setOptions({
            headerTitle: '',
            headerTintColor: colors.text.primary,
            headerRight: () => <HelpScreenButton />,
        });
    }, [colors.text.primary]);
    const networth = useAppSelector(selectNetworth);
    const annualIncomes = useAppSelector(selectAnnualIncome);
    const fundingSource = useAppSelector(selectFundingSource);
    const employmentStatus = useAppSelector(selectEmploymentStatus);
    const dispatch = useAppDispatch();
    useAmplitudeOnMountEvent(undefined, 'User Info Screen', { type: 'finra_disclosures' });
    const onSubmit = useCallback((key) => () => {
        if (networth && annualIncomes && fundingSource && employmentStatus) {
            if (CHOICES_MAP[key]) {
                navigateDeprecated('InvestAffiliatesDetails', {
                    annualIncomeMin: annualIncomes.annualIncomeMin,
                    annualIncomeMax: annualIncomes.annualIncomeMax,
                    liquidNetWorthMin: networth.liquidNetWorthMin,
                    liquidNetWorthMax: networth.liquidNetWorthMax,
                    fundingSource,
                    employmentStatus,
                });
            }
            else {
                const newParams = {
                    annualIncomeMin: annualIncomes.annualIncomeMin,
                    annualIncomeMax: annualIncomes.annualIncomeMax,
                    liquidNetWorthMin: networth.liquidNetWorthMin,
                    liquidNetWorthMax: networth.liquidNetWorthMax,
                    fundingSource,
                    employmentStatus,
                    isAffiliatedExchangeOrFinra: false,
                    isControlPerson: false,
                    isPoliticallyExposed: false,
                };
                dispatch({ type: 'SAVE_ONBOARDING_STATE', screen: 'InvestConditions', params: newParams });
                navigateToNextScreen();
            }
        }
        else {
            const dataObj = {
                annualIncomes: !!annualIncomes,
                networth: !!networth,
                fundingSource: !!fundingSource,
                employmentStatus: !!employmentStatus,
            };
            Sentry.logError({
                filename: 'AffiliatesScreen',
                message: 'User not able to go ahead',
                data: dataObj,
            });
            navigation.popToTop();
        }
    }, [annualIncomes, employmentStatus, fundingSource, navigateToNextScreen, networth]);
    const { paddingHorizontalStyle } = useAppFrameDimensions(params?.mountedOnAnotherScreen);
    const marginBottom = useMarginBottom();
    return (<View style={commonStyles.containerNoPadding}>
      <StatusBar barStyle="dark-content" animated/>
      <InvestHeaderWithHelpButton hideButton={!params?.mountedOnAnotherScreen}/>
      <ScrollView scrollEnabled={false} contentContainerStyle={commonStyles.contentContainer}>
        <ScreenInfoHeader title={onboardingTitleStrings.Affiliations.title}/>
        <SettingsSection noMarginTop>
          <SettingsItemBase icon={<IconSirenOnSolid />}>
            <SettingsItemContent>
              <SettingsItemDescription>{strings[0]}</SettingsItemDescription>
            </SettingsItemContent>
          </SettingsItemBase>
          <SettingsItemBase icon={<IconWork gradientKey="skyBlue"/>}>
            <SettingsItemContent>
              <SettingsItemDescription>{strings[1]}</SettingsItemDescription>
            </SettingsItemContent>
          </SettingsItemBase>
          <SettingsItemBase icon={<IconBank />}>
            <SettingsItemContent>
              <SettingsItemDescription>{strings[2]}</SettingsItemDescription>
            </SettingsItemContent>
          </SettingsItemBase>
        </SettingsSection>
      </ScrollView>
      <View style={[
            params?.mountedOnAnotherScreen && !paddingHorizontalStyle ? styles.btnGroupWithoutSafeArea : marginBottom,
            paddingHorizontalStyle,
        ]}>
        <ButtonGroup>
          <TextLink title="Some apply to me" onPress={onSubmit(CHOICES[0])}/>
          <Button brand title="Confirm" onPress={onSubmit(CHOICES[1])}/>
        </ButtonGroup>
      </View>
    </View>);
};
const styles = StyleSheet.create({
    btnGroupWithoutSafeArea: {
        marginBottom: rem(16),
    },
});
export default AffiliatesScreen;
